import { CriteoIntegration } from "./_criteoApi";
import { energyMap } from "./_energyMap";
import { productCardTemplatePLP } from "./_productCardTemplatePLP";

export class CriteoCollectionPage extends CriteoIntegration {
  #response;

  /**
   * Get the cached API response
   * @returns {object|undefined} The Criteo API response
   */
  getResponse() {
    return this.#response;
  }

  #selectors = {
    mainPage: "#page .main-content",
    ulList: ".snize-search-results-content.clearfix",
    results: "snize_results",
    image: "img.snize-item-image",
    title: "span.snize-title",
    price: "span.snize-price.money.bigprices.active",
    paginate: ".snize-pagination ul",
    paginateMobile: ".snize-pagination .snize-current-page",
  };
  #flag = false;
  #prevParams = {};
  #productCardsCreated = [];
  #busy = false;

  /**
   * @returns {Promise<HTMLUListElement>}
   * @description Wait for searchnize render the grid of products and then return it in order
   * to work with the products cards needed
   */
  async #grid() {
    return new Promise((resolve, _reject) => {
      let grid = document.querySelector(this.#selectors.ulList);

      if (!grid) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach(({ type, addedNodes }) => {
            if (type === "childList") {
              addedNodes.forEach((node) => {
                if (node.id === this.#selectors.results) {
                  observer.disconnect();
                  resolve(node.querySelector(this.#selectors.ulList));
                }
              });
            }
          });
        });

        observer.observe(document.querySelector(this.#selectors.mainPage), {
          childList: true,
          subtree: true,
        });
      } else resolve(grid);
    });
  }

  /**
   * @param {'search-results-page' | 'collections'} template
   * @returns {Promise<import("./defs").ICriteoReqParams>}
   */
  async #generateParams(template) {
    const filters = window.location.search
      .split("&")
      .filter(
        (filter) => filter.includes("rb_vendor") || filter.includes("rb_price")
      )
      .reduce((prev, current) => {
        const [key, value] = current.split("=");

        prev[key] = value.replace(/%20/g, " ");

        return prev;
      }, {});

    const filterParam = Object.entries(filters).reduce((prev, [key, value]) => {
      if (key === "rb_price") {
        const [min, max] = value.split("%2C");
        prev += `${prev ? "," : ""}(price,ge,${min}),(price,le,${max})`;
      } else if (key === "rb_vendor") {
        prev += `${prev ? "," : ""}(brand,in,${value.replace("%7C", ",")})`;
      }

      return prev;
    }, "");

    const grid = await this.#grid();
    const skus = Array.from(grid.querySelectorAll("span.snize-sku")).map(
      (span) => span.innerText
    );

    const [prefix, id] = window.criteoCategory?.split("_") || [];

    const params = {
      item: skus.join("|"),
      "list-size": skus.length,
      "page-number": 1,
      ...(filterParam && { filters: filterParam }),
      ...(template === "collections" && { category: `${prefix}_${id}` }),
      ...(template === "search-results-page" && {
        keywords: (() => {
          const urlParams = new URLSearchParams(window.location.search);

          // Check for rb_product_type parameter first
          if (urlParams.has("rb_product_type")) {
            return urlParams.get("rb_product_type").toLowerCase();
          }

          // Otherwise extract from q parameter
          return decodeURI(
            window.location.search
              .replace("?q=", "")
              .split("&")[0]
              .replace(/%20/g, " ")
          );
        })(),
      }),
    };

    let paginationElement = document.querySelector(this.#selectors.paginate);

    if (paginationElement) {
      params["page-number"] = Array.from(paginationElement.childNodes).find(
        (liEl) => {
          const anchor = liEl.querySelector("a");
          if (anchor) return anchor.classList?.contains("active");
        }
      ).innerText;
    } else {
      paginationElement = document.querySelector(
        this.#selectors.paginateMobile
      );

      if (paginationElement) {
        const [page] = paginationElement.innerText.split("de");

        params["page-number"] = Number(page);
      }
    }

    // if page has pagination then add the current page to the params for the criteo request

    const paramsHasChanges = !!Object.entries(params).find(
      ([key, value]) =>
        key !== "item" &&
        key !== "list-size" &&
        (!this.#prevParams[key] || value !== this.#prevParams[key])
    );

    paramsHasChanges &&
      ((this.#response = null),
      (this.#productCardsCreated = []),
      (this.#flag = false));

    this.#prevParams = params;

    return params;
  }

  /**
   * @param {'search-results-page' | 'collections'} template
   */
  async exec(template) {
    try {
      if (!this._isActive) return;

      // Get the grid of products
      const grid = await this.#grid();
      const params = await this.#generateParams(template);

      if (!this.#response)
        this.#response = await this._baseRequest(
          template === "collections" ? "viewCategory" : "viewSearchResult",
          params
        );

      // Request to criteo API to get the sponsored products
      const { placements } = this.#response;

      if (!placements.length) return;

      const device = screen.width <= 750 ? "Mobile" : "Desktop";

      const viewType = template === "collections" ? "Category" : "SearchResult";

      const [
        {
          [`view${viewType}Api${device}-InGrid01`]: [
            {
              products: criteoGridOne = [],
              OnLoadBeacon: OnLoadBeaconGridOne,
              OnViewBeacon: OnViewBeaconGridOne,
            },
          ] = [{}],
        } = {},
        {
          [`view${viewType}Api${device}-InGrid02`]: [
            {
              products: criteoGridTwo = [],
              OnLoadBeacon: OnLoadBeaconGridTwo,
              OnViewBeacon: OnViewBeaconGridTwo,
            },
          ] = [{}],
        } = {},
      ] = placements;

      criteoGridOne[0] &&
        (criteoGridOne[0].placementViewBeacon = OnViewBeaconGridOne);

      criteoGridTwo[0] &&
        (criteoGridTwo[0].placementViewBeacon = OnViewBeaconGridTwo);

      // Merge products and include new prop position based on grid structure from response
      const productsMerged = [...criteoGridOne, ...criteoGridTwo].map(
        (product, i, arr) => ({
          ...product,
          position: i === arr.length - 1 && arr.length === 3 ? 4 : i,
        })
      );

      /**
       * @type { { productsIncluded: Node[], productsMissing: import("./defs").Product[]}}
       */
      const { productsIncluded, productsMissing } = productsMerged.reduce(
        (prev, current) => {
          const productCardAlreadyCreated = this.#productCardsCreated.find(
            (el) => {
              const sku = el.querySelector("span.snize-sku").innerText;
              return sku === current.ProductId.toString();
            }
          );

          if (productCardAlreadyCreated) {
            const sameCard = Array.from(grid.childNodes).find((productCard) => {
              const clonedSku =
                productCard.querySelector("span.snize-sku").innerText;
              const sku =
                productCardAlreadyCreated.querySelector(
                  "span.snize-sku"
                ).innerText;

              return (
                sku === clonedSku && productCard !== productCardAlreadyCreated
              );
            });

            sameCard && sameCard.remove();

            prev.productsIncluded.push(productCardAlreadyCreated);
          } else {
            // Find a product card with the same product from criteo

            const sameProductInGrid = Array.from(grid.childNodes).find(
              (productCard) => {
                const sku =
                  productCard.querySelector("span.snize-sku").innerText;
                return sku === current.ProductId.toString();
              }
            );

            if (sameProductInGrid) {
              sameProductInGrid.dataset.criteoPosition = current.position;

              sameProductInGrid.dataset.criteoViewBeacon = current.OnViewBeacon;

              current.placementViewBeacon &&
                (sameProductInGrid.dataset.criteoPlacementViewBeacon =
                  current.placementViewBeacon);

              const cloned = sameProductInGrid.cloneNode(true);

              const productCardLink = cloned.querySelector("a.snize-view-link");

              productCardLink.dataset.criteoOnClickBeacon =
                current.OnClickBeacon;

              prev.productsIncluded.push(cloned);

              this.#productCardsCreated.push(cloned);

              sameProductInGrid.remove();
            } else {
              const isIncluded = prev.productsMissing.find((product) => {
                product.ProductId === current.ProductId;
              });

              if (!isIncluded) prev.productsMissing.push(current);
            }
          }
          return prev;
        },
        {
          productsIncluded: [],
          productsMissing: [],
        }
      );

      const productCards = [...productsIncluded];

      if (productsMissing.length) {
        // Creates an array with just the SKUs of the products missing
        const criteoProductsSKUs = productsMissing.map(
          (product) => product.ProductId
        );

        // retrieve product info from Shopify and merge it with the info of criteo
        /**
         * @type {(import("./defs").IShopiProduct & { criteo: import("./defs").Product })[]}
         */
        const shopifyProducts = (
          await this._getProducts(criteoProductsSKUs)
        ).map((product) => {
          const sameProduct = productsMissing.find((missing) => {
            const [{ sku }] = product.variants.nodes;
            return missing.ProductId === sku;
          });

          return { ...product, ...(sameProduct && { criteo: sameProduct }) };
        });

        // Iterates over each shopify product and append it to the grid
        const newProductCards = shopifyProducts.map(
          ({
            collections: { nodes: collections },
            vendor,
            tags,
            title,
            onlineStoreUrl,
            id: productId,
            featuredImage: { src },
            criteo: {
              position,
              OnViewBeacon,
              OnClickBeacon,
              ProductId,
              placementViewBeacon,
            } = {},
            compareAtPriceRange: {
              minVariantPrice: { amount: compareAtPrice },
            },
            priceRange: {
              minVariantPrice: { amount: price },
            },
          }) => {
            const productCardCloned = document.createElement("li");

            productCardCloned.innerHTML = productCardTemplatePLP;

            // Create the new clases for the product card
            const newClasses = `snize-product snize-product-in-stock snize-product-vendor-${vendor} ${tags
              .map((tag) => `snize-product-tag-${tag.toLowerCase()}`)
              .join(" ")}`;

            // change attributes of the cloned product card
            productCardCloned.id = `snize-product-${productId.replace(
              /gid:\/\/.*\//,
              ""
            )}`;

            productCardCloned.className = newClasses;

            productCardCloned.querySelector(this.#selectors.image).src = src;

            productCardCloned.querySelector(this.#selectors.title).innerText =
              title;

            productCardCloned.querySelector("a:first-child").href =
              onlineStoreUrl;

            productCardCloned.dataset.criteoPosition = position;

            productCardCloned.dataset.criteoViewBeacon = OnViewBeacon;

            productCardCloned.querySelector(
              'div[data-bv-show="rating_summary"]'
            ).dataset.bvProductId = ProductId;

            const productCardLink =
              productCardCloned.querySelector("a.snize-view-link");

            productCardLink.dataset.criteoOnClickBeacon = OnClickBeacon;

            placementViewBeacon &&
              (productCardCloned.dataset.criteoPlacementViewBeacon =
                placementViewBeacon);

            const compareAtFormated = this._formatPrice(
              parseFloat(compareAtPrice)
            );
            const priceFormated = this._formatPrice(parseFloat(price));

            const priceListTemplate = `
              <span class="snize-price ${
                parseFloat(compareAtPrice) > parseFloat(price)
                  ? "snize-price-with-discount"
                  : ""
              } money" data-formated="true">
              ${priceFormated}
              </span>
              ${
                parseFloat(compareAtPrice) > parseFloat(price)
                  ? `
                  <span class="snize-discounted-price money">
                    ${compareAtFormated}
                  </span>`
                  : ""
              }
              <span class="snize-save-price-wrapper">
              </span>
              <span class="price-tax">
                IGIC Incl.
              </span>
            `;

            productCardCloned.querySelector(".snize-price-list").innerHTML =
              priceListTemplate;

            productCardCloned.querySelector(".snize-attribute").innerHTML =
              collections.map((collection) => collection.title).join(", ");

            productCardCloned.querySelector("span.snize-sku").innerText =
              ProductId;

            grid.appendChild(productCardCloned);
            this.#productCardsCreated.push(productCardCloned);

            return productCardCloned;
          }
        );

        /**
         * NOTE: Uncomment below code if you want to show energetic labels in product cards as in PT.
         * Define the relationship between tags and images as in searchnise config in the _energyMap.js file.
         * See ticket IPE-26977
         */

        // const [_, imageUrl] = Object.entries(energyMap).find(([key]) =>
        //   tags.includes(key)
        // );

        // const imageLabel = productCardCloned.querySelector(
        //   ".snize-product-label-image"
        // );

        // if (imageUrl) {
        //   const imageTag = imageLabel.querySelector("img");

        //   imageTag.src = imageUrl;
        // } else {
        //   imageLabel.remove();
        // }

        productCards.push(...newProductCards);
      }

      // Sort the product cards
      productCards.sort((a, b) => {
        const aPos = Number(a.dataset.criteoPosition);
        const bPos = Number(b.dataset.criteoPosition);

        return aPos < bPos ? -1 : 1;
      });

      productCards.forEach((card) => {
        !card.querySelector(".sponsored-criteo-container") &&
          this._sponsoredBadge(card);
        const position = Number(card.dataset.criteoPosition);
        grid.insertBefore(card, grid.childNodes.item(position));
      });

      const onLoadBeacons = productsMerged.map((prod) => prod.OnLoadBeacon);

      if (!this.#flag) {
        this._onLoadEvent([
          ...onLoadBeacons,
          OnLoadBeaconGridOne,
          OnLoadBeaconGridTwo,
        ]);
        this._viewPortEvent(productCards);
        this._onClickEvent(
          productCards.map((card) => card.querySelector("a.snize-view-link"))
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      this.#flag = true;
    }
  }
}
